import React, { useRef, useEffect, useState, Fragment } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import filter from '../../utils/filter';

import { Icons, InputBox } from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';

import Popup from '../Popup';

type ItemListChoice = {
  id: any;
  label: React.ReactNode;
  metric?: React.ReactNode;
  href: string;
};

const DEFAULT_TARGET = (selectedChoice, searchText, props) => {
  const TargetIcon = props.targetIcon || Icons.Search;
  return (
    <InputBox
      type="text"
      placeholder={props.placeholder}
      defaultValue={selectedChoice ? selectedChoice.label : searchText}
      leftIcon={<TargetIcon color={colorVariables.gray400} />}
      onFocus={e => e.preventDefault()}
      width="100%"
      style={{ pointerEvents: 'none' }}
    />
  );
};

// ------------------------------------------------------------------------------
// Searchable Select Box
//
// Use this control to implement a select box that has a search built in.
// ------------------------------------------------------------------------------
type SearchableSelectBoxProps = {
  placeholder?: string;
  selectedId: ItemListChoice['id'];
  popupWidth?: React.ReactText;
  choices: Array<ItemListChoice>;
  target?: (
    selectedChoice: ItemListChoice,
    searchText: string,
    props: SearchableSelectBoxProps,
  ) => React.ReactNode;
  targetIcon?: React.FunctionComponent;
};

const SearchableSelectBox: React.FunctionComponent<SearchableSelectBoxProps> = props => {
  const { choices, ...restProps } = props;

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');

  // When the selected id changes, close the popup
  useEffect(() => {
    setOpen(false);
    setSearchText('');
  }, [ restProps.selectedId ]);

  const selectedChoice = choices.find(choice => choice.id === restProps.selectedId);

  const filteredChoices = filter<ItemListChoice>(choices, searchText, [
    'label', 'id',
  ]);

  return (
    <AsyncSearchableSelectBox
      {...restProps}
      choices={filteredChoices}

      searchValue={searchText}
      onSearchChange={e => setSearchText(e.target.value)}

      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    />
  );
};


// ------------------------------------------------------------------------------
// Async Searchable Select Box
//
// Use this control when you need to control the search process in a more fine
// grained fashion. Instead of implementing the fuzzy searching logic, it calls
// props to tell the parent context about updates, and the parent context is
// responsible for updating the choices / etc.
// ------------------------------------------------------------------------------

type AsyncSearchableSelectBoxProps = SearchableSelectBoxProps & {
  searchValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  open: boolean,
  onOpen: () => void;
  onClose: () => void;
};

export const AsyncSearchableSelectBox: React.FunctionComponent<AsyncSearchableSelectBoxProps> = props => {
  const {
    placeholder,
    choices,
    selectedId,
    target = DEFAULT_TARGET,
    popupWidth = 300,

    searchValue,
    onSearchChange,

    open,
    onOpen,
    onClose,
  } = props;
  const TargetIcon = (props.targetIcon || Icons.Search) as any;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // When the selected id changes, close the popup
  useEffect(() => {
    onClose();
  }, [selectedId]);

  // Ensure that the select box is always focused
  const searchBox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchBox.current && open) {
      searchBox.current.focus();
    }
  });

  const selectedChoice = choices.find(choice => choice.id === selectedId);

  function keyPress(e) {
    let newSelectedIndex: number;
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (selectedIndex === null) {
        setSelectedIndex(0);
        return;
      }

      newSelectedIndex = selectedIndex + 1;
      if (newSelectedIndex > choices.length - 1) {
        newSelectedIndex = choices.length - 1;
      }
      setSelectedIndex(newSelectedIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (selectedIndex === null) {
        return;
      }

      newSelectedIndex = selectedIndex - 1;
      if (newSelectedIndex < 0) {
        newSelectedIndex = 0;
      }
      setSelectedIndex(newSelectedIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();

      let newSelectedIndex = selectedIndex;
      if (selectedIndex === null) {
        newSelectedIndex = 0;
      }

      if (choices[newSelectedIndex].id === selectedId) {
        onClose();
        return;
      }

      const href = choices[newSelectedIndex].href;

      // When holding down ctrl / command, open in a new tab (just like clicking)
      if (e.ctrlKey || e.metaKey) {
        window.open(href, '_blank');
      } else {
        window.location.href = href;
      }
    }
  }

  return (
    <Popup
      target={target(selectedChoice, searchValue, props)}
      open={open}
      popupWidth={popupWidth}
      position={{ top: -9, left: -9 }}
      onOpen={() => {
        setSelectedIndex(null);
        onOpen();
      }}
      onClose={() => onClose()}
    >
      <div className={styles.searchableSelectBoxPopup}>
        <InputBox
          type="text"
          value={searchValue}
          placeholder={placeholder}
          onChange={e => {
            setSelectedIndex(null);
            if (onSearchChange) {
              onSearchChange(e);
            }
          }}
          leftIcon={<TargetIcon color={colorVariables.gray400} />}
          onKeyDown={keyPress}
          width="100%"
          ref={searchBox}
          data-test-id="searchable-select-box-search-box"
        />
      </div>

      {choices.length > 0 ? (
        <ul
          className={styles.itemList}
          onMouseLeave={() => setSelectedIndex(null)}
        >
          {choices.map((choice, index) => (
            <a
              key={choice.id}
              href={choice.href}
              tabIndex={-1} // Don't want these to be tab focusable
              onClick={() => {
                if (choice.id === selectedId) {
                  onClose();
                }
              }}
            >
              <li
                className={classnames({
                  [styles.selected]: selectedIndex === index,
                })}
                onFocus={() => setSelectedIndex(index)}
                onMouseEnter={() => setSelectedIndex(index)}
                data-test-id="searchable-select-box-list-item"
              >
                <span className={styles.label}>{choice.label}</span>
                {choice.metric || ''}
              </li>
            </a>
          ))}
        </ul>
      ) : (
        <div className={styles.emptyState}>No matches found</div>
      )}
    </Popup>
  );
};

export default SearchableSelectBox;
